// frontend/src/pages/admin/ServiceCategoriesManagement.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalAdminStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const ServiceCategoriesManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    value: "",
    label: "",
    description: "",
  });

  useEffect(() => {
    checkPortalAdmin();
    fetchBusinessTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPortalAdmin = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return;
      }
    } catch (error) {
      navigate("/login");
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/business-categories/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setBusinessTypes(response.data.types || []);
      if (response.data.types?.length > 0) {
        setSelectedBusinessType(response.data.types[0]);
      }
      setLoading(false);
    } catch (error) {
      showMessage("Failed to load business types", "error");
      setLoading(false);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ value: "", label: "", description: "" });
    setShowModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      value: category.value,
      label: category.label,
      description: category.description || "",
    });
    setShowModal(true);
  };

  const handleDeleteCategory = async (categoryValue) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the service category "${categoryValue}"?`,
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");

      // Update business type's service categories
      const updatedCategories = selectedBusinessType.serviceCategories.filter(
        (cat) => cat.value !== categoryValue,
      );

      const updatedBusinessTypes = businessTypes.map((bt) =>
        bt.value === selectedBusinessType.value
          ? { ...bt, serviceCategories: updatedCategories }
          : bt,
      );

      await axios.put(
        `${API_BASE_URL}/api/platform-settings`,
        {
          businessCategories: updatedBusinessTypes,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setBusinessTypes(updatedBusinessTypes);
      setSelectedBusinessType({
        ...selectedBusinessType,
        serviceCategories: updatedCategories,
      });
      showMessage("Service category deleted successfully", "success");
    } catch (error) {
      showMessage("Failed to delete service category", "error");
    }
  };

  const handleExportCategories = () => {
    if (
      !selectedBusinessType ||
      !selectedBusinessType.serviceCategories?.length
    ) {
      showMessage("No service categories to export", "error");
      return;
    }

    const exportData = {
      businessType: {
        value: selectedBusinessType.value,
        label: selectedBusinessType.label,
        emoji: selectedBusinessType.emoji,
      },
      serviceCategories: selectedBusinessType.serviceCategories,
      exportedAt: new Date().toISOString(),
      version: "1.0",
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `service-categories-${selectedBusinessType.value}-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showMessage("Service categories exported successfully", "success");
  };

  const handleImportCategories = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const importData = JSON.parse(text);

        // Validate import data structure
        if (
          !importData.serviceCategories ||
          !Array.isArray(importData.serviceCategories)
        ) {
          showMessage(
            "Invalid import file: missing serviceCategories array",
            "error",
          );
          return;
        }

        // Validate each category has required fields
        const invalidCategories = importData.serviceCategories.filter(
          (cat) => !cat.value || !cat.label,
        );
        if (invalidCategories.length > 0) {
          showMessage(
            "Invalid import file: some categories missing required fields (value, label)",
            "error",
          );
          return;
        }

        // Merge with existing categories (avoid duplicates by value)
        const existingValues = new Set(
          selectedBusinessType.serviceCategories.map((cat) => cat.value),
        );
        const newCategories = importData.serviceCategories.filter(
          (cat) => !existingValues.has(cat.value),
        );

        if (newCategories.length === 0) {
          showMessage(
            "No new categories to import (all already exist)",
            "error",
          );
          return;
        }

        const confirmMsg =
          `Import ${newCategories.length} new service categories?\n\n` +
          newCategories
            .map((cat) => `• ${cat.label} (${cat.value})`)
            .join("\n");

        if (!window.confirm(confirmMsg)) {
          return;
        }

        const token = localStorage.getItem("ogestyle_token");
        const updatedCategories = [
          ...selectedBusinessType.serviceCategories,
          ...newCategories,
        ];
        const updatedBusinessTypes = businessTypes.map((bt) =>
          bt.value === selectedBusinessType.value
            ? { ...bt, serviceCategories: updatedCategories }
            : bt,
        );

        await axios.put(
          `${API_BASE_URL}/api/platform-settings`,
          {
            businessCategories: updatedBusinessTypes,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setBusinessTypes(updatedBusinessTypes);
        setSelectedBusinessType({
          ...selectedBusinessType,
          serviceCategories: updatedCategories,
        });
        showMessage(
          `Successfully imported ${newCategories.length} service categories`,
          "success",
        );
      } catch (error) {
        showMessage(
          "Failed to import service categories: " + error.message,
          "error",
        );
      }
    };
    input.click();
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    if (!categoryForm.value || !categoryForm.label) {
      showMessage("Please fill in all required fields", "error");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("ogestyle_token");

      let updatedCategories;
      if (editingCategory) {
        // Update existing category
        updatedCategories = selectedBusinessType.serviceCategories.map((cat) =>
          cat.value === editingCategory.value ? categoryForm : cat,
        );
      } else {
        // Add new category
        if (
          selectedBusinessType.serviceCategories.some(
            (cat) => cat.value === categoryForm.value,
          )
        ) {
          showMessage("A category with this value already exists", "error");
          setSaving(false);
          return;
        }
        updatedCategories = [
          ...selectedBusinessType.serviceCategories,
          categoryForm,
        ];
      }

      const updatedBusinessTypes = businessTypes.map((bt) =>
        bt.value === selectedBusinessType.value
          ? { ...bt, serviceCategories: updatedCategories }
          : bt,
      );

      await axios.put(
        `${API_BASE_URL}/api/platform-settings`,
        {
          businessCategories: updatedBusinessTypes,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setBusinessTypes(updatedBusinessTypes);
      setSelectedBusinessType({
        ...selectedBusinessType,
        serviceCategories: updatedCategories,
      });
      setShowModal(false);
      showMessage(
        editingCategory
          ? "Category updated successfully"
          : "Category added successfully",
        "success",
      );
    } catch (error) {
      showMessage("Failed to save category", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <button
            onClick={() => navigate("/portal-admin")}
            style={{
              background: "none",
              border: "none",
              color: "#001d4a",
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 0",
              marginBottom: "16px",
            }}
          >
            ← Back to Portal Admin
          </button>
          <h1
            style={{ fontSize: "32px", fontWeight: "700", margin: "0 0 8px" }}
          >
            📂 Service Categories Management
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            Manage service categories for each business type
          </p>
        </div>

        {message.text && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: message.type === "success" ? "#d1fae5" : "#fee2e2",
              border: `1px solid ${message.type === "success" ? "#10b981" : "#ef4444"}`,
              color: message.type === "success" ? "#065f46" : "#991b1b",
            }}
          >
            {message.text}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "300px 1fr",
            gap: "24px",
          }}
        >
          {/* Business Types Sidebar */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "24px",
              height: "fit-content",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Business Types
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {businessTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSelectedBusinessType(type)}
                  style={{
                    padding: "12px",
                    border: "1px solid",
                    borderColor:
                      selectedBusinessType?.value === type.value
                        ? "#3b82f6"
                        : "#e5e7eb",
                    borderRadius: "8px",
                    background:
                      selectedBusinessType?.value === type.value
                        ? "#eff6ff"
                        : "white",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight:
                      selectedBusinessType?.value === type.value
                        ? "600"
                        : "400",
                    color:
                      selectedBusinessType?.value === type.value
                        ? "#1e40af"
                        : "#374151",
                  }}
                >
                  {type.emoji} {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Service Categories Content */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "32px",
            }}
          >
            {selectedBusinessType ? (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                  }}
                >
                  <div>
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "600",
                        margin: "0 0 4px",
                      }}
                    >
                      {selectedBusinessType.emoji} {selectedBusinessType.label}
                    </h2>
                    <p
                      style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}
                    >
                      {selectedBusinessType.serviceCategories?.length || 0}{" "}
                      service categories
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button
                      onClick={handleExportCategories}
                      disabled={!selectedBusinessType.serviceCategories?.length}
                      style={{
                        padding: "10px 20px",
                        background: selectedBusinessType.serviceCategories
                          ?.length
                          ? "#10b981"
                          : "#e5e7eb",
                        color: selectedBusinessType.serviceCategories?.length
                          ? "white"
                          : "#9ca3af",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: selectedBusinessType.serviceCategories?.length
                          ? "pointer"
                          : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      title="Export service categories to JSON file"
                    >
                      📥 Export
                    </button>
                    <button
                      onClick={handleImportCategories}
                      style={{
                        padding: "10px 20px",
                        background: "#f59e0b",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      title="Import service categories from JSON file"
                    >
                      📤 Import
                    </button>
                    <button
                      onClick={handleAddCategory}
                      style={{
                        padding: "10px 20px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      + Add Category
                    </button>
                  </div>
                </div>

                {selectedBusinessType.serviceCategories?.length > 0 ? (
                  <div style={{ display: "grid", gap: "12px" }}>
                    {selectedBusinessType.serviceCategories.map((category) => (
                      <div
                        key={category.value}
                        style={{
                          padding: "16px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <h4
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              margin: "0 0 4px",
                            }}
                          >
                            {category.label}
                          </h4>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              margin: "0 0 4px",
                            }}
                          >
                            Value:{" "}
                            <code
                              style={{
                                background: "#f3f4f6",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              {category.value}
                            </code>
                          </p>
                          {category.description && (
                            <p
                              style={{
                                fontSize: "14px",
                                color: "#374151",
                                margin: "8px 0 0",
                              }}
                            >
                              {category.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            onClick={() => handleEditCategory(category)}
                            style={{
                              padding: "8px 16px",
                              background: "#eff6ff",
                              color: "#3b82f6",
                              border: "1px solid #3b82f6",
                              borderRadius: "6px",
                              fontSize: "14px",
                              cursor: "pointer",
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.value)}
                            style={{
                              padding: "8px 16px",
                              background: "#fef2f2",
                              color: "#dc2626",
                              border: "1px solid #dc2626",
                              borderRadius: "6px",
                              fontSize: "14px",
                              cursor: "pointer",
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#6b7280",
                      border: "2px dashed #e5e7eb",
                      borderRadius: "8px",
                    }}
                  >
                    <p style={{ fontSize: "16px", margin: "0 0 16px" }}>
                      No service categories yet
                    </p>
                    <button
                      onClick={handleAddCategory}
                      style={{
                        padding: "10px 20px",
                        background: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Add First Category
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#6b7280",
                }}
              >
                Select a business type to manage its service categories
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "32px",
                width: "500px",
                maxWidth: "90%",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "24px",
                }}
              >
                {editingCategory
                  ? "Edit Service Category"
                  : "Add Service Category"}
              </h3>

              <form onSubmit={handleSaveCategory}>
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Value (code) *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.value}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        value: e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "-"),
                      })
                    }
                    placeholder="e.g., haircut"
                    disabled={!!editingCategory}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      background: editingCategory ? "#f3f4f6" : "white",
                    }}
                    required
                  />
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: "4px 0 0",
                    }}
                  >
                    Lowercase, no spaces (use hyphens)
                  </p>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Label (display name) *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.label}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        label: e.target.value,
                      })
                    }
                    placeholder="e.g., Haircut"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Description (optional)
                  </label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        description: e.target.value,
                      })
                    }
                    placeholder="Brief description of this category"
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                      resize: "vertical",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: saving ? "#9ca3af" : "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: saving ? "not-allowed" : "pointer",
                    }}
                  >
                    {saving ? "Saving..." : editingCategory ? "Update" : "Add"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "white",
                      color: "#6b7280",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: saving ? "not-allowed" : "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default ServiceCategoriesManagement;
